<template>
  <div class="roles-table">
    <table class="roles-table__table">
      <thead class="roles-table__header">
        <tr>
          <th class="roles-table__th">Role</th>
          <th class="roles-table__th">Number of Users Assigned</th>
          <th class="roles-table__th">Date Created</th>
          <th class="roles-table__th roles-table__th--actions">Actions</th>
        </tr>
      </thead>
      <tbody class="roles-table__body">
        <tr v-for="role in roles" :key="role.id" class="roles-table__row">
          <td class="roles-table__td">
            <span class="roles-table__role-name">{{ role.name }}</span>
          </td>
          <td class="roles-table__td">
            <span class="roles-table__users-count">{{ role.teamMembers?.length || 0 }} Users</span>
            Assigned
          </td>
          <td class="roles-table__td">
            {{ formatDate(role.createdAt) }}
          </td>
          <td class="roles-table__td roles-table__td--actions">
            <Popper offsetDistance="5">
              <button class="roles-table__action-trigger" :disabled="!isActionAllowed">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                </svg>
              </button>
              <template #content="{ close }">
                <div class="roles-table__dropdown">
                  <ul class="roles-table__dropdown-list">
                    <TeamMembersModalButton
                      :role="role"
                      title="View Team Members"
                      @member-removed="onAction"
                      :close="close"
                    />
                    <li>
                      <button
                        @click="editRole(role.id, close)"
                        class="roles-table__dropdown-item"
                        :disabled="!isActionAllowed"
                      >
                        Edit Role
                      </button>
                    </li>
                    <li>
                      <button
                        @click="deleteRole(role.id)"
                        class="roles-table__dropdown-item roles-table__dropdown-item--danger"
                        :disabled="!isActionAllowed"
                      >
                        Delete Role
                      </button>
                    </li>
                  </ul>
                </div>
              </template>
            </Popper>
          </td>
        </tr>
        <tr v-if="roles.length === 0 && !loading">
          <td colspan="4" class="roles-table__empty">
            No roles found matching your search.
          </td>
        </tr>
        <tr v-if="loading">
          <td colspan="4" class="roles-table__loading">
            <svg class="roles-table__spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="roles-table__spinner-track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="roles-table__spinner-head" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
  
  <script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import TeamMembersModalButton from "../TeamMembersModalButton.vue";
import Pagination from "@/components/navigation/Pagination";
import Popper from "vue3-popper";

export default {
  props: {
    roles: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    onAction: {
      type: Function,
      required: false,
      default: null,
    },
    totalPages: { type: Number, default: () => 0, required: true },
    currentPage: { type: Number, default: () => 0, required: true },
    isActionAllowed: Boolean,
  },

  emits: ["edit-role", "delete-role", "gotoPage"],

  components: {
    TeamMembersModalButton,
    Pagination,
    Popper,
  },

  setup(props, { emit }) {
    const searchQuery = ref("");

    function formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString();
    }

    function editRole(roleId, closeFn) {
      emit("edit-role", roleId);

      closeFn();
    }

    function deleteRole(roleId) {
      emit("delete-role", roleId);
    }

    const gotoPage = (page) => {
      this.$emit("gotoPage", page);
    };

    return {
      formatDate,
      editRole,
      deleteRole,
      gotoPage,
    };
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-subtle: #F8FAFC;
$bg-hover: #F1F5F9;
$border-color: #E2E8F0;
$error: #DC2626;
$error-light: #FEF2F2;

.roles-table {
  width: 100%;
  overflow-x: auto;

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__th {
    padding: 16px 24px;
    text-align: left;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: $text-muted;
    background: $bg-subtle;
    border-bottom: 1px solid $border-color;

    &--actions {
      text-align: right;
    }
  }

  &__row {
    transition: background 0.15s ease;

    &:hover {
      background: $bg-hover;
    }
  }

  &__td {
    padding: 20px 24px;
    font-size: 14px;
    color: $text-secondary;
    border-bottom: 1px solid $border-color;
    white-space: nowrap;

    &--actions {
      text-align: right;
    }
  }

  &__role-name {
    font-weight: 500;
    color: $text-primary;
  }

  &__users-count {
    font-weight: 500;
  }

  &__action-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: transparent;
    border: 1px solid transparent;
    color: $text-muted;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: $bg-subtle;
      border-color: $border-color;
      color: $text-primary;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__dropdown {
    min-width: 200px;
    padding: 8px;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px solid $border-color;
  }

  &__dropdown-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__dropdown-item {
    display: block;
    width: 100%;
    padding: 12px 14px;
    font-size: 14px;
    font-weight: 450;
    color: $text-secondary;
    background: none;
    border: none;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: $bg-subtle;
      color: $text-primary;
    }

    &--danger {
      color: $error;

      &:hover {
        background: $error-light;
        color: $error;
      }
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__empty {
    padding: 40px 24px;
    text-align: center;
    color: $text-muted;
    font-size: 14px;
  }

  &__loading {
    padding: 40px 24px;
    text-align: center;
  }

  &__spinner {
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
    margin: 0 auto;
    display: block;
  }

  &__spinner-track {
    opacity: 0.25;
  }

  &__spinner-head {
    opacity: 0.75;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}
</style>